/**
 * Did the request fail, or did the BROWSER just stop waiting?
 *
 * WHY THIS EXISTS
 * ---------------
 * A deploy/apply renders, validates and applies every manifest before it
 * answers. On a busy cluster that runs well past the client's default 30s
 * timeout — and the server keeps going after the client gives up. On
 * 2026-09-22 a customer saw "Apply failed — timeout of 30000ms exceeded" for an
 * apply that cluster-api completed successfully seconds later.
 *
 * So a timeout (or a dropped connection with no response at all) is NOT a
 * verdict. The only verdict is the instance's own state, which the wizard
 * already polls. Anything the server actually answered — 402, 409, 422, 500 —
 * IS a verdict and must surface unchanged.
 *
 * Pure and dependency-free on purpose: it is the one rule both the API client
 * and the wizard consult, and it is unit-testable without the app.
 */
export function isApplyStillRunning(error) {
  if (!error) return false
  if (error.response) return false // the server answered — a real verdict
  return (
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT' ||
    /timeout/i.test(error.message || '')
  )
}

/** How long a deploy/apply call may wait before the client stops listening. */
export const APPLY_TIMEOUT_MS = 180000

/* ===========================================================================
 * Is the apply finished? — the other half of the same rule
 * ===========================================================================
 *
 * The note above says "the only verdict is the instance's own state, which the
 * wizard already polls". It did not: step 4 polled
 * `GET /uts/instances/{id}/preflight-status` and nothing else.
 *
 * That endpoint reads the `preflight_deployments` collection, written ONLY by
 * the cs-application deploy arm (cluster-api `routes/uts.py` gates on
 * `template_id == 'cs-application'`, NOT on `deployment_standard` — a catalog
 * cs-application is `direct` and DOES get one). A plain catalog stack template
 * is deployed by `TemplateDeployer` via kubectl apply, has no pipeline
 * anywhere, and gets a PERMANENT 404:
 *
 *   {"message":"no preflight deployment for instance 'ti_…'","status_code":404}
 *
 * Axios rejects on 4xx, the poller swallowed it as a transient blip, and the
 * screen sat on "In progress — Rolling out changes" forever: neither the green
 * nor the red exit was reachable and there was no attempt cap. Measured on
 * `ti_6ba232610aa143728bd71654b3f18083` (mongodb-sb, site-b) while the cluster
 * already read 3/3 pods Running.
 *
 * The old exit could not have worked for a cs-application either: it tested for
 * `green` / `healthy` / `deployed` / `failed`, and the real preflight enum
 * (cluster-api `services/PreflightStatusScheduler.py`) is `completed`,
 * `skipped_running_healthy`, `rolled_back`, `blocked`, `pipeline_failed`,
 * `workload_failed`, `stuck`, `timeout`, `superseded` — none of those strings.
 *
 * WHICH FIELD MEANS "THE APPLY FINISHED"
 * --------------------------------------
 * `deploy_state` is the rollout's own progress: `deploying` → `healthy` /
 * `degraded` / `failed`. For a `TemplateDeployer` rollout it is already
 * terminal when the deploy POST returns, so one read settles it.
 *
 * The exception, and why the pod-readiness rescue below is load-bearing: on a
 * cs-application rollout `CsApplicationDeployer` sets `deploy_state` to
 * `deploying` and nothing ever flips it back (the reconciler leaves the field
 * alone by design). There, preflight is the completion signal and
 * `reconciled_state` + pod readiness is the backstop.
 *
 * `reconciled_state` is what the reconciler last OBSERVED in the cluster. It is
 * health, not progress, and can sit at `degraded` for reasons unrelated to this
 * apply. Gating on it would reintroduce the hang, so we use it two ways only: a
 * `failed` reading is believed (never mask a real failure), and a green reading
 * rescues the stuck-`deploying` case above.
 *
 * `upgrade_state` is never a rollout signal — it tracks whether a newer catalog
 * template version exists.
 */

import { activeBranchState, podCounts } from './instance-shape.js'

/** `deploy_state` values that mean the rollout is still running. */
const DEPLOY_IN_FLIGHT = new Set([
  'deploying', 'rolling_back', 'pending', 'queued', 'in_progress'
])

/** States that mean the rollout is over and it did not work. */
const FAILED_STATES = new Set(['failed', 'error'])

/** `reconciled_state` readings that mean "the cluster looks right". */
const RECON_GREEN = new Set(['healthy', 'healthy_no_pipeline', 'deployed'])

/**
 * Readings that mean "the apply landed, but the app is not fully well". Not a
 * failure of the apply itself — the detail page is where this gets diagnosed.
 */
const RECON_SOFT = new Set([
  'degraded', 'warning', 'drifted', 'healthy_with_failed_pipeline'
])

// ─────────────────────────── preflight applicability ───────────────────────

/**
 * Does a preflight deployment exist for the rollout we just started?
 *
 * The deploy response is the authority: only the cs-application arm returns a
 * `preflight` block (`doc_id` / `deploys[]`). A `TemplateDeployer` response has
 * no `preflight` key at all, so its `preflight-status` is a guaranteed 404.
 *
 * `deployResult` is null when the deploy POST timed out (see
 * `isApplyStillRunning`) — then we infer from the instance, and when we still
 * cannot tell we return true: one poll costs a single 404, which
 * `pollErrorKind` turns off permanently, whereas wrongly skipping preflight on
 * a cs-application loses the only completion signal it has.
 *
 * @param {object|null} deployResult unwrapped `data` from `deployInstance()`
 * @param {object|null} inst         the instance (or `{template_id}`) to infer from
 */
export function preflightApplicable(deployResult, inst = null) {
  const pf = deployResult?.preflight
  if (pf && (pf.doc_id || (Array.isArray(pf.deploys) && pf.deploys.length))) return true
  // A deploy response WITHOUT a preflight block is positive evidence of none.
  if (deployResult && typeof deployResult === 'object') return false

  const verdict = String(inst?.preflight_verdict ?? '').trim().toLowerCase()
  if (verdict === 'na' || verdict === 'none' || verdict === 'not_applicable') return false

  const template = String(inst?.template_id ?? '').trim().toLowerCase()
  const appType = String(inst?.app_type ?? '').trim().toLowerCase()
  if (template === 'cs-application' || appType === 'cs-application') {
    return String(inst?.deployment_standard ?? '').trim().toLowerCase() !== 'denv-standard'
  }
  if (template || appType) return false // a known, non-cs-application template
  return true // genuinely unknown — poll once; a 404 settles it
}

/**
 * Classify a failed poll request.
 *
 *   'absent'    → the resource does not exist for this instance. A fact, not a
 *                 blip; stop asking and fall back to instance state.
 *   'permanent' → retrying cannot change the answer (bad request, no longer
 *                 authorised, method gone). Also stop asking.
 *   'transient' → server wobble, timeout, offline. Worth another tick.
 */
export function pollErrorKind(error) {
  const status = error?.response?.status
  if (status === 404 || status === 410 || status === 501) return 'absent'
  if (typeof status === 'number' && status >= 400 && status < 500) return 'permanent'
  return 'transient'
}

// ───────────────────────────── preflight verdicts ──────────────────────────

/** Preflight finished and the new version is serving. */
const PF_GOOD = new Set(['completed', 'skipped_running_healthy'])

/** Preflight finished and the change did not land. */
const PF_BAD = new Set([
  'blocked', 'rolled_back', 'pipeline_failed', 'workload_failed',
  'stuck', 'stuck_pending', 'timeout', 'failed'
])

/** Finished, but with no verdict of its own — another deploy took over. */
const PF_INERT = new Set(['superseded', 'stopped'])

/**
 * Read a `preflight-status` payload.
 *
 * @returns {object|null} an outcome, or null while preflight is still running —
 *   the instance leg then has its say.
 */
export function preflightOutcome(pf, isEdit = true) {
  const raw = String(pf?.status ?? pf?.light ?? '').trim().toLowerCase()
  if (!raw) return null
  if (PF_GOOD.has(raw) || raw === 'green') {
    return {
      phase: 'done',
      light: 'green',
      title: isEdit ? 'Changes applied' : 'Your app is live',
      label: 'Healthy',
      status: isEdit ? 'Your changes are live.' : 'Your app is running.',
      reason: ''
    }
  }
  if (PF_BAD.has(raw) || raw === 'red') {
    return {
      phase: 'failed',
      light: 'red',
      title: isEdit ? 'Apply failed' : 'Deployment failed',
      label: 'Failed',
      status: '',
      reason:
        pf?.message ||
        pf?.reason ||
        (raw === 'rolled_back'
          ? 'The rollout was rolled back — your app is still on the previous version.'
          : raw === 'blocked'
            ? 'A preflight check blocked the rollout.'
            : 'The rollout did not complete.')
    }
  }
  if (PF_INERT.has(raw)) {
    return {
      phase: 'done',
      light: 'unknown',
      title: isEdit ? 'Changes applied' : 'App deployed',
      label: 'Superseded',
      status: 'Another deploy took over this rollout. Open the app for its current status.',
      reason: ''
    }
  }
  return null
}

// ──────────────────────────── rollout completion ───────────────────────────

/**
 * Pod readiness from whichever shape the backend gave us. `reconciled_branches`
 * is null on a freshly reconciled catalog stack, in which case the live counts
 * arrive under the `/full` payload's `general.live_pod_summary`.
 *
 * @returns {{ready:number, desired:number}}
 */
export function readiness(inst, general) {
  const live = general?.live_pod_summary
  if (live && (typeof live.desired_replicas === 'number' || typeof live.pod_count === 'number')) {
    const desired = typeof live.desired_replicas === 'number' ? live.desired_replicas : live.pod_count
    const ready = typeof live.ready_replicas === 'number' ? live.ready_replicas : (live.running || 0)
    return { ready: ready || 0, desired: desired || 0 }
  }
  return podCounts(inst)
}

/** True when every pod the app asked for is ready. */
function allPodsReady(inst, general) {
  const { ready, desired } = readiness(inst, general)
  return desired > 0 && ready >= desired
}

function reconState(inst, general) {
  const branch = activeBranchState(inst)
  if (branch) return branch
  const agg = inst?.reconciled_state ?? general?.reconciled_state
  return agg ? String(agg).trim().toLowerCase() : ''
}

function deployState(inst, general) {
  const ds = inst?.deploy_state ?? general?.deploy_state
  return ds ? String(ds).trim().toLowerCase() : ''
}

/**
 * Decide whether the apply is over, from the instance document alone.
 *
 * @param {object} inst    the instance doc (`/uts/instances/{id}` or `.instance`)
 * @param {object} general optional `/full` → `general` block (live pod counts)
 * @param {boolean} isEdit true for the edit-apply wording, false for a first deploy
 * @returns {{phase:'in_progress'|'done'|'failed', light:'green'|'yellow'|'red'|'unknown',
 *            title:string, label:string, status:string, reason:string}}
 *   `title` is step 4's headline — one honest sentence per state, so the
 *   "Applying…" wording can never outlive the rollout. `light: 'unknown'` is
 *   the still, non-spinning orb: finished, but not a clean green. Never return
 *   a pulsing yellow for a finished rollout.
 */
export function applyOutcome(inst, general = null, isEdit = true) {
  const verb = isEdit ? 'change' : 'deployment'
  const ds = deployState(inst, general)
  const rs = reconState(inst, general)

  // 1. A real failure is always a failure. Checked first so nothing below can
  //    sweep it into "assume healthy".
  if (FAILED_STATES.has(ds)) {
    return {
      phase: 'failed',
      light: 'red',
      title: isEdit ? 'Apply failed' : 'Deployment failed',
      label: 'Failed',
      status: '',
      reason: isEdit
        ? 'The rollout failed. Your configuration was saved, but the new version is not running.'
        : 'The deployment failed.'
    }
  }
  if (FAILED_STATES.has(rs)) {
    return {
      phase: 'failed',
      light: 'red',
      title: isEdit ? 'Apply failed' : 'Deployment failed',
      label: 'Failed',
      status: '',
      reason: `The cluster reports this app as failed after the ${verb}.`
    }
  }

  const rolloutRunning = DEPLOY_IN_FLIGHT.has(ds) || (!ds && !rs)

  if (rolloutRunning) {
    // cs-application rollouts never flip `deploy_state` back from `deploying`.
    // Believe the cluster when it says the app is healthy and every pod is up.
    if (RECON_GREEN.has(rs) && allPodsReady(inst, general)) {
      return {
        phase: 'done',
        light: 'green',
        title: isEdit ? 'Changes applied' : 'Your app is live',
        label: 'Healthy',
        status: isEdit ? 'Your changes are live.' : 'Your app is running.',
        reason: ''
      }
    }
    return {
      phase: 'in_progress',
      light: 'yellow',
      title: isEdit ? 'Applying your changes' : 'Deploying your app',
      label: 'In progress',
      status: isEdit ? 'Rolling out your changes…' : 'Starting your app…',
      reason: ''
    }
  }

  // 2. The rollout is over. How well it went is a separate question.
  if (RECON_GREEN.has(rs) || (!rs && ds === 'healthy')) {
    return {
      phase: 'done',
      light: 'green',
      title: isEdit ? 'Changes applied' : 'Your app is live',
      label: 'Healthy',
      status: isEdit ? 'Your changes are live.' : 'Your app is running.',
      reason: ''
    }
  }

  if (RECON_SOFT.has(rs)) {
    return {
      phase: 'done',
      light: 'unknown',
      title: isEdit ? 'Changes applied' : 'App deployed',
      label: 'Applied',
      status: isEdit
        ? 'Your changes are live. The app is not fully healthy yet — open it to see which component is behind.'
        : 'Your app is deployed but not fully healthy yet — open it to see which component is behind.',
      reason: ''
    }
  }

  // Terminal `deploy_state` we have no reading for (suspended, or a new value).
  // Say so rather than spinning.
  return {
    phase: 'done',
    light: 'unknown',
    title: isEdit ? 'Changes applied' : 'App deployed',
    label: 'Applied',
    status: `Your ${verb} was applied. Open the app for its current status.`,
    reason: ''
  }
}

/**
 * The honest terminal state when the poll budget runs out. The apply was very
 * likely fine — we simply stopped being able to confirm it, and saying
 * "In progress" forever would be a lie.
 */
export function timeoutOutcome(isEdit = true) {
  return {
    phase: 'done',
    light: 'unknown',
    title: isEdit ? 'Rollout not confirmed' : 'Deployment not confirmed',
    label: 'Not confirmed',
    status: isEdit
      ? 'Your changes were saved and the rollout was started, but we could not confirm it finished. Open the app to see its current status.'
      : 'Your app was created and the deployment was started, but we could not confirm it finished. Open the app to see its current status.',
    reason: ''
  }
}

/**
 * Poll budget. 3s ticks for 5 minutes — comfortably longer than a catalog
 * rollout, short enough that nobody watches a dead screen for a working day.
 */
export const POLL_INTERVAL_MS = 3000
export const POLL_MAX_MS = 5 * 60 * 1000
export const POLL_MAX_ATTEMPTS = Math.ceil(POLL_MAX_MS / POLL_INTERVAL_MS)

export default {
  isApplyStillRunning,
  APPLY_TIMEOUT_MS,
  preflightApplicable,
  pollErrorKind,
  preflightOutcome,
  applyOutcome,
  timeoutOutcome,
  readiness,
  POLL_INTERVAL_MS,
  POLL_MAX_MS,
  POLL_MAX_ATTEMPTS
}
